import { useState, useEffect } from 'react'
import {
  Building2, Users, Plus, Trash2, ChevronRight, ChevronDown,
  Loader2, Shield, UserPlus, Pencil, Check, X, AlertCircle,
} from 'lucide-react'
import api from '@/shared/lib/api'
import { useAuthStore } from '@/core/stores/auth'
import { cn } from '@/shared/lib/utils'

interface Team {
  id: string
  name: string
  description: string | null
  member_count: number
  created_at: string | null
}

interface TeamDetail {
  id: string
  name: string
  description: string | null
  members: TeamMember[]
}

interface TeamMember {
  id: string
  user_id: string
  email: string
  full_name: string | null
  role: string
  job_title: string | null
  joined_at: string | null
}

const roleBadge = (role: string) => {
  const cfg: Record<string, string> = {
    owner: 'bg-gold-light text-gold-dark',
    admin: 'bg-violet/10 text-violet',
    manager: 'bg-blue-100 text-blue-700',
    member: 'bg-paper-warm text-ink-muted',
  }
  return (
    <span className={cn('text-[10px] font-semibold px-2 py-0.5 rounded-full', cfg[role] || cfg.member)}>
      {role}
    </span>
  )
}

// ── Create Team Modal ─────────────────────────────────

function CreateTeamModal({ onClose, onCreated }: { onClose: () => void; onCreated: () => void }) {
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  const handleCreate = async () => {
    if (!name.trim()) return
    setLoading(true)
    setError('')
    try {
      await api.post('/teams', { name: name.trim(), description: description.trim() || null })
      onCreated()
      onClose()
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Erreur')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 backdrop-blur-sm">
      <div className="bg-white rounded-2xl border border-ink-border shadow-elevated w-full max-w-md p-6 animate-slide-up">
        <div className="flex items-center justify-between mb-5">
          <h3 className="text-base font-semibold text-ink">Nouvelle equipe</h3>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-paper-warm"><X size={16} className="text-ink-faint" /></button>
        </div>
        <div className="space-y-4">
          <div>
            <label className="block text-xs font-medium text-ink-muted mb-1">Nom de l'equipe</label>
            <input type="text" value={name} onChange={(e) => setName(e.target.value)} placeholder="Marketing, SEO, Support..."
              className="w-full px-3 py-2.5 border border-ink-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-gold/50 focus:border-gold" />
          </div>
          <div>
            <label className="block text-xs font-medium text-ink-muted mb-1">Description (optionnel)</label>
            <input type="text" value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Equipe en charge de..."
              className="w-full px-3 py-2.5 border border-ink-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-gold/50 focus:border-gold" />
          </div>
          {error && <p className="text-xs text-red-600">{error}</p>}
          <div className="flex justify-end gap-2">
            <button onClick={onClose} className="px-4 py-2 text-sm text-ink-muted hover:bg-paper-warm rounded-lg">Annuler</button>
            <button onClick={handleCreate} disabled={loading || !name.trim()}
              className="flex items-center gap-2 px-5 py-2 text-sm font-semibold text-white bg-brick hover:bg-brick-hover rounded-lg disabled:opacity-50">
              {loading && <Loader2 size={14} className="animate-spin" />} Creer
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

// ── Team Card (expandable with members) ──────────────

function TeamCard({ team, onRefresh }: { team: Team; onRefresh: () => void }) {
  const [expanded, setExpanded] = useState(false)
  const [detail, setDetail] = useState<TeamDetail | null>(null)
  const [loadingDetail, setLoadingDetail] = useState(false)
  const [deleting, setDeleting] = useState(false)

  const toggleExpand = async () => {
    if (expanded) {
      setExpanded(false)
      return
    }
    setExpanded(true)
    if (!detail) {
      setLoadingDetail(true)
      try {
        const { data } = await api.get(`/teams/${team.id}`)
        setDetail(data.data)
      } catch { /* */ }
      finally { setLoadingDetail(false) }
    }
  }

  const handleDelete = async () => {
    if (!confirm(`Supprimer l'equipe "${team.name}" ? Les membres ne seront pas supprimes.`)) return
    setDeleting(true)
    try {
      await api.delete(`/teams/${team.id}`)
      onRefresh()
    } catch { /* */ }
    finally { setDeleting(false) }
  }

  const handleRemoveMember = async (userId: string) => {
    try {
      await api.delete(`/teams/${team.id}/members/${userId}`)
      const { data } = await api.get(`/teams/${team.id}`)
      setDetail(data.data)
      onRefresh()
    } catch { /* */ }
  }

  return (
    <div className="bg-white rounded-card border border-ink-border overflow-hidden">
      <div className="flex items-center gap-3 px-5 py-4 cursor-pointer hover:bg-paper/50" onClick={toggleExpand}>
        <div className="w-9 h-9 rounded-lg bg-gold-light flex items-center justify-center">
          <Users size={18} className="text-gold-dark" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-ink">{team.name}</p>
          {team.description && <p className="text-[11px] text-ink-faint truncate">{team.description}</p>}
        </div>
        <span className="text-xs text-ink-faint bg-paper-warm px-2 py-0.5 rounded-full">{team.member_count} membre(s)</span>
        {expanded ? <ChevronDown size={16} className="text-ink-faint" /> : <ChevronRight size={16} className="text-ink-faint" />}
      </div>

      {expanded && (
        <div className="border-t border-ink-border/30 px-5 py-4 bg-paper/30">
          {loadingDetail ? (
            <div className="flex items-center justify-center py-4"><Loader2 size={18} className="animate-spin text-ink-faint" /></div>
          ) : detail?.members.length === 0 ? (
            <p className="text-xs text-ink-faint text-center py-4">Aucun membre dans cette equipe</p>
          ) : (
            <div className="space-y-2">
              {detail?.members.map((m) => (
                <div key={m.id} className="flex items-center gap-3 py-2">
                  <div className="w-7 h-7 rounded-full bg-gold-light flex items-center justify-center text-[11px] font-bold text-brick">
                    {(m.full_name || m.email)[0].toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="text-sm font-medium text-ink truncate">{m.full_name || m.email}</p>
                      {roleBadge(m.role)}
                    </div>
                    <p className="text-[11px] text-ink-faint">{m.email}{m.job_title ? ` — ${m.job_title}` : ''}</p>
                  </div>
                  <button onClick={(e) => { e.stopPropagation(); handleRemoveMember(m.user_id) }}
                    className="p-1 rounded text-ink-faint hover:text-red-500 hover:bg-red-50" title="Retirer de l'equipe">
                    <X size={14} />
                  </button>
                </div>
              ))}
            </div>
          )}

          <div className="flex items-center justify-between mt-3 pt-3 border-t border-ink-border/30">
            <button onClick={handleDelete} disabled={deleting}
              className="flex items-center gap-1.5 text-[11px] text-red-500 hover:text-red-700 font-medium">
              <Trash2 size={12} /> Supprimer l'equipe
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

// ══════════════════════════════════════════════════════════
//  MAIN PAGE
// ══════════════════════════════════════════════════════════

export function OrganizationSettingsPage() {
  const user = useAuthStore((s) => s.user)
  const [teams, setTeams] = useState<Team[]>([])
  const [loading, setLoading] = useState(true)
  const [showCreateTeam, setShowCreateTeam] = useState(false)

  const fetchTeams = async () => {
    try {
      const { data } = await api.get('/teams')
      setTeams(data.data || [])
    } catch { /* */ }
    finally { setLoading(false) }
  }

  useEffect(() => { fetchTeams() }, [])

  return (
    <div className="space-y-6 max-w-3xl">
      {/* Header */}
      <div>
        <h1 className="font-serif text-2xl font-bold text-ink tracking-tight">Organisation</h1>
        <p className="text-ink-muted mt-1">Gerez vos equipes et leurs membres.</p>
      </div>

      {/* Org Info Card */}
      <div className="bg-white rounded-card border border-ink-border p-5">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-xl bg-onyx flex items-center justify-center">
            <Building2 size={24} className="text-gold" />
          </div>
          <div className="flex-1">
            <p className="text-base font-semibold text-ink">Votre organisation</p>
            <p className="text-xs text-ink-faint mt-0.5">
              {teams.length} equipe(s) — {teams.reduce((s, t) => s + t.member_count, 0)} membre(s) au total
            </p>
          </div>
        </div>
      </div>

      {/* Teams Section */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-base font-semibold text-ink">Equipes</h2>
          <button onClick={() => setShowCreateTeam(true)}
            className="flex items-center gap-2 px-4 py-2 text-sm font-semibold text-white bg-brick hover:bg-brick-hover rounded-lg transition-colors">
            <Plus size={16} /> Nouvelle equipe
          </button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 size={24} className="animate-spin text-ink-faint" />
          </div>
        ) : teams.length === 0 ? (
          <div className="bg-white rounded-card border border-ink-border p-8 text-center">
            <Users size={32} className="mx-auto text-ink-border mb-3" />
            <p className="text-sm text-ink-muted">Aucune equipe creee</p>
            <p className="text-xs text-ink-faint mt-1">Creez votre premiere equipe pour organiser vos collaborateurs.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {teams.map((team) => (
              <TeamCard key={team.id} team={team} onRefresh={fetchTeams} />
            ))}
          </div>
        )}
      </div>

      {/* Create Team Modal */}
      {showCreateTeam && (
        <CreateTeamModal onClose={() => setShowCreateTeam(false)} onCreated={fetchTeams} />
      )}
    </div>
  )
}
